import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery, gql, useMutation } from '@apollo/client';
import {
    Header, Button, Form, Input, TextArea,
    Divider, Label, Message, Loader, Container
} from 'semantic-ui-react';

import { MediaTile } from '../../../components/MediaTile';
import { useContentReviewerGate } from '../../../hooks/isContentReviewer';
import { PATHS } from '../../../paths';
import { parseApiDate } from '../../../utils/date';
import { POST_TITLE_LIMIT, POST_DESCRIPTION_LIMIT, characterCountColour } from '../../../constants';
import { StreamBadge, STREAM_BADGE_FIELDS } from '../../../components/StreamBadge';

// Post is a union (CommunityUpdate | CommunityPrayerRequest | LeadershipUpdate
// | LeadershipPrayerRequest | Birthday); fields must be selected per member.
const POST_SCHEDULED_FIELDS = `
    id
    title
    description
    type
    publishAt
    ${STREAM_BADGE_FIELDS}
    images { id url contentType durationSeconds }
`;

const GET_SCHEDULED_POST = gql`
     query ScheduledPost($postId: Int!) {
         scheduledPost(id: $postId) {
             ... on CommunityUpdate { ${POST_SCHEDULED_FIELDS} }
             ... on CommunityPrayerRequest { ${POST_SCHEDULED_FIELDS} }
             ... on LeadershipUpdate { ${POST_SCHEDULED_FIELDS} }
             ... on LeadershipPrayerRequest { ${POST_SCHEDULED_FIELDS} }
             ... on Birthday { ${POST_SCHEDULED_FIELDS} }
         }
     }
`;

const UPDATE_SCHEDULED_POST = gql`
    mutation UpdateScheduledPost($id: Int!, $title: String!, $description: String!, $publishAt: String!) {
        updateScheduledPost(id: $id, title: $title, description: $description, publishAt: $publishAt) {
            ... on CommunityUpdate { id }
            ... on CommunityPrayerRequest { id }
            ... on LeadershipUpdate { id }
            ... on LeadershipPrayerRequest { id }
            ... on Birthday { id }
        }
    }
`;

const DELETE_SCHEDULED_POST = gql`
    mutation DeleteScheduledPost($id: Int!) {
        deleteScheduledPost(id: $id) {
            ... on CommunityUpdate { id }
            ... on CommunityPrayerRequest { id }
            ... on LeadershipUpdate { id }
            ... on LeadershipPrayerRequest { id }
            ... on Birthday { id }
        }
    }
`;

/** A Date → what a `datetime-local` input needs, in the browser's own timezone. Mirrors
 *  PostCompose.tsx's identical helper. */
const getLocalISOString = (date: Date) => {
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
};

type EditableFields = { title: string; description: string; publishAt: string };

// --- Component ---

const PostScheduledView: React.FC = () => {
    const { postId } = useParams<{ postId: string }>();
    const navigate = useNavigate();
    const { loading: meLoading, isContentReviewer } = useContentReviewerGate();
    const [isExiting, setIsExiting] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    const { data, loading, error } = useQuery(GET_SCHEDULED_POST, {
        variables: { postId: Number(postId) },
        skip: !postId
    });

    // Seeded once from the query result, then edited locally until Save.
    const [form, setForm] = useState<EditableFields | null>(null);
    useEffect(() => {
        if (data?.scheduledPost && form == null) {
            const publishDate = parseApiDate(data.scheduledPost.publishAt);
            setForm({
                title: data.scheduledPost.title || '',
                description: data.scheduledPost.description || '',
                publishAt: getLocalISOString(publishDate || new Date()),
            });
        }
    }, [data, form]);

    const [updatePost, { loading: saving }] = useMutation(UPDATE_SCHEDULED_POST);
    const [deletePost, { loading: deleting }] = useMutation(DELETE_SCHEDULED_POST);

    if (!meLoading && !isContentReviewer) {
        return <Navigate to={PATHS.CLASSES} replace />;
    }

    const handleSave = async () => {
        if (!form) return;
        setActionError(null);
        try {
            await updatePost({
                variables: {
                    id: Number(postId),
                    title: form.title,
                    description: form.description,
                    publishAt: new Date(form.publishAt).toISOString(),
                }
            });
            setIsExiting(true);
            setTimeout(() => navigate(PATHS.POSTS_SCHEDULED), 500);
        } catch (err: any) {
            setActionError(err?.message || 'Could not save changes.');
        }
    };

    const handleDelete = async () => {
        setActionError(null);
        try {
            await deletePost({ variables: { id: Number(postId) } });
            setIsExiting(true);
            setTimeout(() => navigate(PATHS.POSTS_SCHEDULED), 500);
        } catch (err: any) {
            setActionError(err?.message || 'Could not delete this post.');
        }
    };

    if (loading || !form) return <Loader active inline='centered' style={{ marginTop: '5em' }}>Loading post...</Loader>;
    if (error || !data?.scheduledPost) return <Message error content="Could not load this scheduled post." />;

    const { scheduledPost: post } = data;
    const busy = saving || deleting;
    const overLimit = form.title.length > POST_TITLE_LIMIT || form.description.length > POST_DESCRIPTION_LIMIT;

    return (
        <Container
            text
            style={{
                padding: '3em 0em',
                transition: 'opacity 0.5s ease-in-out',
                opacity: isExiting ? 0 : 1
            }}
        >
            <Label basic color="grey" size="small" style={{ marginBottom: '1em' }}>{post.type}</Label>
            <div style={{ marginBottom: '10px' }}>
                <StreamBadge stream={post.stream} size="small" />
            </div>
            <Header as="h1" style={{ marginTop: 0 }}>Edit scheduled post</Header>

            {actionError && <Message error content={actionError} />}

            {post.images.length > 0 && (
                <div style={{ display: 'flex', overflowX: 'auto', gap: '12px', paddingBottom: '15px', marginBottom: '2em' }}>
                    {post.images.map((img: any) => (
                        <MediaTile
                            key={img.id}
                            url={img.url}
                            contentType={img.contentType}
                            durationSeconds={img.durationSeconds}
                            rounded
                            bordered
                            controls
                            style={{ height: '180px', objectFit: 'cover' }}
                        />
                    ))}
                </div>
            )}

            <Form>
                <Form.Field required>
                    <label>Title</label>
                    <Input
                        value={form.title}
                        disabled={busy}
                        onChange={(_, { value }) => setForm(f => f && ({ ...f, title: value }))}
                    />
                    <div style={{ textAlign: 'right', fontSize: '0.8em', color: characterCountColour(form.title.length, POST_TITLE_LIMIT) }}>
                        {form.title.length} / {POST_TITLE_LIMIT}
                    </div>
                </Form.Field>

                <Form.Field required>
                    <label>Description</label>
                    <TextArea
                        rows={6}
                        value={form.description}
                        disabled={busy}
                        onChange={(_, { value }) => setForm(f => f && ({ ...f, description: value as string }))}
                    />
                    <div style={{ textAlign: 'right', fontSize: '0.8em', color: characterCountColour(form.description.length, POST_DESCRIPTION_LIMIT) }}>
                        {form.description.length} / {POST_DESCRIPTION_LIMIT}
                    </div>
                </Form.Field>

                <Form.Field required>
                    <label>Publish date &amp; time</label>
                    <Input
                        type='datetime-local'
                        value={form.publishAt}
                        disabled={busy}
                        onChange={(_, { value }) => setForm(f => f && ({ ...f, publishAt: value }))}
                    />
                </Form.Field>
            </Form>

            <Divider style={{ margin: '3em 0 2em 0' }} />

            <div style={{ display: 'flex', gap: '20px' }}>
                <Button
                    basic
                    color="red"
                    size="huge"
                    fluid
                    icon="trash"
                    labelPosition="left"
                    content="Delete"
                    loading={deleting}
                    disabled={isExiting || busy}
                    onClick={handleDelete}
                />
                <Button
                    positive
                    size="huge"
                    fluid
                    icon="checkmark"
                    labelPosition="left"
                    content="Save changes"
                    loading={saving}
                    disabled={isExiting || busy || !form.title || !form.description || overLimit}
                    onClick={handleSave}
                />
            </div>
        </Container>
    );
};

export { PostScheduledView };
